/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableRow,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from "@tanstack/react-query";
import { StyledBox, StyledSearchBar } from "../../reports/global_employee_search/styles";
import { scrollbarStyles } from "../../reports/styles";
import { StyledTitle, StyledTitleContainer } from "../../restaurant_groups/styles";
import { StyledSubTitle } from "../../employee_groups/group_members/styles";
import {
  StyledTableBodyCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
} from "../../../components/index_table/styles";
import api from "../../../api";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { dynamicDiscountMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/dynamicDiscountMutationKeys";
import { Loader } from "../../../components/loader";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { useFillViewportHeight } from "../../../hooks/useFillViewportHeight";
import { AbilityContext } from "../../../contexts/abilityContext";
import { Query_Keys } from "../../../lib/queryKeys";
import { strictCanScope } from "../../../utils/caslStrict";
import { checkAdmin } from "../../../utils/checkAdmin";
import { normalizeSearchTerm } from "../../../utils/normalizeSearchTerm";
import moment from "moment";
import { useSelector } from "react-redux";
import { formatDynamicDiscountScopeLabel } from "../scope_labels";

const TABLE_STORAGE_KEY = 'tableWidths-dynamic-discount-applicability'
const EVENT_COLUMN_KEY = 'event-name'
const EVENT_COLUMN_WIDTH = 260
const CARD_COLUMN_WIDTH = 140
const EVENT_HEADER_HEIGHT = 88
const SLOT_ROW_HEIGHT = 60
const EVENT_HEADER_PADDING_Y = 1.25

const formatSlotLabel = (slot) => {
  if (slot?.all_day) {
    return 'All day'
  }

  const startTime = slot?.start_time ? moment(slot.start_time, 'HH:mm').format('hh:mm A') : ''
  const endTime = slot?.end_time ? moment(slot.end_time, 'HH:mm').format('hh:mm A') : ''

  if (!startTime || !endTime) {
    return 'Custom time'
  }

  return `${startTime} - ${endTime}`
}

const formatDateRange = (event) => {
  const startDate = event?.start_date ? moment(event.start_date).format('MMM D, YYYY') : ''
  const endDate = event?.end_date ? moment(event.end_date).format('MMM D, YYYY') : ''

  if (!startDate || !endDate) {
    return ''
  }

  return `${startDate} - ${endDate}`
}

const buildEmptyStateContainerSx = (theme) => ({
  borderRadius: '20px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: 4,
  textAlign: 'center',
})

const buildEmptyStateTitleSx = (theme, color) => ({
  fontSize: '15px',
  color,
  fontWeight: 600,
  marginBottom: 1,
})

const buildEmptyStateBodySx = (theme) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
})

const buildSectionDividerSx = (theme) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
})

const buildSlotDividerSx = (theme, index) => ({
  borderTop: index === 0 ? 'none' : `1px solid ${theme.palette.divider}`,
})

const buildMatrixCellGridSx = (slotCount) => ({
  display: 'grid',
  gridTemplateRows: [`${EVENT_HEADER_HEIGHT}px`, ...Array.from({ length: slotCount }, () => `${SLOT_ROW_HEIGHT}px`)].join(' '),
  height: EVENT_HEADER_HEIGHT + (slotCount * SLOT_ROW_HEIGHT),
})

const ScopeChip = ({ eventId, scopeType }) => {
  const theme = useTheme()
  const label = formatDynamicDiscountScopeLabel(scopeType, {
    groupLabel: 'Group Level Event',
    restaurantLabel: 'Restaurant Level Event',
  })

  if (!label) {
    return null
  }

  const scopePalette = scopeType === 'restaurant' ? theme.palette.warning : theme.palette.info

  return (
    <Typography
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        width: 'fit-content',
        maxWidth: '100%',
        fontSize: '11px',
        fontWeight: 700,
        color: scopePalette.dark || scopePalette.main,
        backgroundColor: alpha(scopePalette.main, 0.14),
        borderRadius: '999px',
        px: 1,
        py: 0.25,
        mb: 0.75,
      }}
      data-testid={`matrix-event-scope-${eventId}`}
    >
      {label}
    </Typography>
  )
}

const buildDefaultColumnWidths = (cards) => {
  return cards.reduce((accumulator, card) => {
    accumulator[`card-${card?.id}`] = CARD_COLUMN_WIDTH
    return accumulator
  }, { [EVENT_COLUMN_KEY]: EVENT_COLUMN_WIDTH })
}

const getSlotRows = (event) => {
  if (event?.slots?.length) {
    return event.slots
  }

  return [{ id: `event-${event?.id}-empty-slot`, placeholder: true, cards: [] }]
}

export const DiscountsSection = ({ restaurantGroupIds = [], scopeType, autoScrollToMatrix = false, onAutoScrollComplete }) => {
  const theme = useTheme()
  const showNotification = useNotificationContext()
  const { ability } = useContext(AbilityContext)
  const { admin } = useSelector((state) => state.admin)
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [searchTerm, setSearchTerm] = useState('')
  const [matrixData, setMatrixData] = useState({ events: [], cards: [] })
  const [pendingCellKey, setPendingCellKey] = useState(null)
  const [columnWidths, setColumnWidths] = useState({ [EVENT_COLUMN_KEY]: EVENT_COLUMN_WIDTH })
  const [scrollWidth, setScrollWidth] = useState(0)
  const tableRef = useRef(null)
  const matrixSectionRef = useRef(null)
  const fakeScrollRef = useRef(null)
  const tableFooterRef = useRef(null)

  const containerMaxHeight = useFillViewportHeight({
    containerRef: tableRef,
    bottomRef: tableFooterRef,
  })

  const { mutateAsync: toggleDiscountEventBlockMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/dynamic_discount_events/toggle', payload),
    invalidateKeys: [...dynamicDiscountMutationKeyGroups.update],
  })
  const resizingRef = useRef({ columnKey: null, startX: 0, startWidth: 0 })

  const normalizedRestaurantGroupIds = useMemo(
    () => Array.from(new Set((restaurantGroupIds || []).filter(Boolean))),
    [restaurantGroupIds]
  )

  const fetchMatrix = async () => {
    const response = await api.get('/v1/admin/dynamic_discount_events/matrix', {
      params: {
        restaurant_group_ids: normalizedRestaurantGroupIds.length ? normalizedRestaurantGroupIds : undefined,
        scope_type: scopeType || undefined,
      }
    })

    return response?.data
  }

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: Query_Keys.dynamic_discount_events_matrix({
      restaurant_group_ids: normalizedRestaurantGroupIds,
      scope_type: scopeType,
    }),
    queryFn: fetchMatrix,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (data) {
      setMatrixData({
        events: data?.events || [],
        cards: data?.cards || [],
      })
    }
  }, [data])

  const resolvedMatrixData = useMemo(() => {
    const hasLocalEvents = (matrixData?.events || []).length > 0
    const hasLocalCards = (matrixData?.cards || []).length > 0

    if (hasLocalEvents || hasLocalCards || pendingCellKey) {
      return matrixData
    }

    return {
      events: data?.events || [],
      cards: data?.cards || [],
    }
  }, [data, matrixData, pendingCellKey])

  const cardColumns = useMemo(() => {
    return (resolvedMatrixData?.cards || []).flatMap((group) => {
      return (group?.cards || []).map((card) => ({
        ...card,
        restaurant_group_id: group?.restaurant_group_id,
      }))
    })
  }, [resolvedMatrixData?.cards])

  const columns = useMemo(() => {
    return [
      { key: EVENT_COLUMN_KEY, minWidth: EVENT_COLUMN_WIDTH },
      ...cardColumns.map((card) => ({
        key: `card-${card?.id}`,
        minWidth: CARD_COLUMN_WIDTH,
        color: card?.color,
        label: card?.name,
      })),
    ]
  }, [cardColumns])

  const filteredEvents = useMemo(() => {
    const normalizedSearch = normalizeSearchTerm(searchTerm)

    if (!normalizedSearch) {
      return resolvedMatrixData?.events || []
    }

    return (resolvedMatrixData?.events || []).filter((event) => {
      const nameMatches = normalizeSearchTerm(event?.name).includes(normalizedSearch)
      const slotMatches = getSlotRows(event).some((slot) => {
        const slotLabel = slot?.placeholder ? 'No time slots' : formatSlotLabel(slot)
        return normalizeSearchTerm(slotLabel).includes(normalizedSearch)
      })

      return nameMatches || slotMatches
    })
  }, [resolvedMatrixData?.events, searchTerm])

  useEffect(() => {
    const defaultWidths = buildDefaultColumnWidths(cardColumns)

    let storedWidths = {}
    try {
      storedWidths = JSON.parse(localStorage.getItem(TABLE_STORAGE_KEY) || '{}')
    } catch (_error) {
      storedWidths = {}
    }

    const nextWidths = Object.keys(defaultWidths).reduce((accumulator, key) => {
      accumulator[key] = storedWidths[key] || defaultWidths[key]
      return accumulator
    }, {})

    setColumnWidths((currentValue) => {
      const sameKeys = Object.keys(currentValue).length === Object.keys(nextWidths).length
      const sameValues = sameKeys && Object.keys(nextWidths).every((key) => currentValue[key] === nextWidths[key])
      return sameValues ? currentValue : nextWidths
    })
  }, [cardColumns])

  const handleMainScroll = useCallback(() => {
    if (!tableRef.current || !fakeScrollRef.current) {
      return
    }

    fakeScrollRef.current.scrollLeft = tableRef.current.scrollLeft
  }, [])

  const handleFakeScroll = useCallback(() => {
    if (!tableRef.current || !fakeScrollRef.current) {
      return
    }

    tableRef.current.scrollLeft = fakeScrollRef.current.scrollLeft
  }, [])

  const handleMouseMove = useCallback((mouseEvent) => {
    const { columnKey, startX, startWidth } = resizingRef.current
    if (!columnKey) {
      return
    }

    const column = columns.find((currentColumn) => currentColumn.key === columnKey)
    const minWidth = column?.minWidth || 80
    const nextWidth = Math.max(minWidth, startWidth + mouseEvent.clientX - startX)

    setColumnWidths((currentValue) => ({
      ...currentValue,
      [columnKey]: nextWidth,
    }))
  }, [columns])

  const handleMouseUp = useCallback((mouseEvent) => {
    const { columnKey, startX, startWidth } = resizingRef.current
    if (!columnKey) {
      return
    }

    const column = columns.find((currentColumn) => currentColumn.key === columnKey)
    const minWidth = column?.minWidth || 80
    const finalWidth = Math.max(minWidth, startWidth + mouseEvent.clientX - startX)

    setColumnWidths((currentValue) => {
      const nextWidths = {
        ...currentValue,
        [columnKey]: finalWidth,
      }

      localStorage.setItem(TABLE_STORAGE_KEY, JSON.stringify(nextWidths))
      return nextWidths
    })

    resizingRef.current = { columnKey: null, startX: 0, startWidth: 0 }
    document.body.style.cursor = ''
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }, [columns, handleMouseMove])

  const startResizing = useCallback((columnKey, mouseEvent) => {
    mouseEvent.preventDefault()

    resizingRef.current = {
      columnKey,
      startX: mouseEvent.clientX,
      startWidth: columnWidths[columnKey] || 0,
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
  }, [columnWidths, handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (tableRef.current) {
      setScrollWidth(tableRef.current.scrollWidth)
    }
  }, [columnWidths, filteredEvents])

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setScrollWidth(tableRef.current.scrollWidth)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (!autoScrollToMatrix || isLoading || isError || !matrixSectionRef.current) {
      return
    }

    matrixSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    onAutoScrollComplete?.()
  }, [autoScrollToMatrix, isError, isLoading, onAutoScrollComplete, filteredEvents.length, cardColumns.length])

  const canUpdateDiscountCard = useCallback((event) => {
    if (superAdmin?.role === 'admin') {
      return true
    }

    return strictCanScope(ability, 'update_discount_card', 'Restaurant_Group', `${event?.restaurant_group_id}`)
  }, [ability, superAdmin])

  const updateLocalCell = (eventId, slotId, cardId, blocked) => {
    setMatrixData((currentValue) => ({
      ...currentValue,
      events: (currentValue?.events || []).map((event) => {
        if (event?.id !== eventId) {
          return event
        }

        return {
          ...event,
          slots: (event?.slots || []).map((slot) => {
            if (slot?.id !== slotId) {
              return slot
            }

            return {
              ...slot,
              cards: (slot?.cards || []).map((card) => {
                if (card?.id !== cardId) {
                  return card
                }

                return {
                  ...card,
                  blocked,
                }
              })
            }
          })
        }
      })
    }))
  }

  const handleToggleChange = async (event, slot, card, blocked) => {
    if (!event?.restaurant_group_id || !canUpdateDiscountCard(event)) {
      return
    }

    const cellKey = `${slot?.id}-${card?.id}`
    updateLocalCell(event?.id, slot?.id, card?.id, blocked)
    setPendingCellKey(cellKey)

    try {
      await toggleDiscountEventBlockMutation({
        restaurant_group_id: event?.restaurant_group_id,
        changes: [
          {
            time_slot_id: slot?.id,
            discount_card_id: card?.id,
            blocked: !blocked,
          }
        ]
      });
      showNotification(`Discount applicability updated successfully for the event: ${event?.name}`, 'success')
    } catch (error) {
      updateLocalCell(event?.id, slot?.id, card?.id, !blocked)
      showNotification(error?.response?.data?.error || 'Unable to update discount applicability', 'error')
    } finally {
      setPendingCellKey(null)
    }
  }

  return (
    <Box>
      <StyledBox report={false}>
        <StyledTitleContainer>
          <StyledTitle style={{ fontSize: '20px' }}>Perq</StyledTitle>
          <StyledSubTitle>Eliminate perqs for chosen events.</StyledSubTitle>
        </StyledTitleContainer>

        <StyledSearchBar
          value={searchTerm}
          placeholder="Search events or slots"
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
        />
      </StyledBox>

        {isLoading ? (
          <Loader height={64} sx={{ py: 6 }} />
      ) : isError ? (
        <Box sx={buildEmptyStateContainerSx(theme)}>
          <Typography sx={buildEmptyStateTitleSx(theme, theme.palette.error.dark || theme.palette.error.main)}>
            Unable to load the discount matrix.
          </Typography>
          <Typography sx={buildEmptyStateBodySx(theme)}>
            Refresh the page or try a different restaurant group filter.
          </Typography>
        </Box>
      ) : !cardColumns.length || !filteredEvents.length ? (
        <Box sx={buildEmptyStateContainerSx(theme)}>
          <Typography sx={buildEmptyStateTitleSx(theme, theme.palette.text.primary)}>
            No discount applicability rows found.
          </Typography>
          <Typography sx={buildEmptyStateBodySx(theme)}>
            Adjust the current filters or create new active events and discount cards.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }} ref={matrixSectionRef}>
          <StyledTableContainer
            ref={tableRef}
            onScroll={handleMainScroll}
            sx={{
              mt: 1,
              boxShadow: 'none',
              overflowX: 'scroll',
              overflowY: 'auto',
              maxHeight: containerMaxHeight,
            }}
          >
            <Table
              sx={{
                minWidth: columns.reduce((total, column) => total + (columnWidths[column.key] || column.minWidth || 80), 0),
              }}
              data-testid="discount-applicability-matrix-table"
            >
              <StyledTableHead report={false}>
                <TableRow>
                  <StyledTableHeadCell
                    sx={{
                      width: columnWidths[EVENT_COLUMN_KEY],
                      minWidth: EVENT_COLUMN_WIDTH,
                      maxWidth: columnWidths[EVENT_COLUMN_KEY],
                      userSelect: 'none',
                    }}
                  >
                    Event name
                    <Box
                      onMouseDown={(mouseEvent) => startResizing(EVENT_COLUMN_KEY, mouseEvent)}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        height: '100%',
                        width: '6px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
                      }}
                    />
                  </StyledTableHeadCell>
                  {cardColumns.map((card) => (
                    <StyledTableHeadCell
                      key={card?.id}
                      sx={{
                        width: columnWidths[`card-${card?.id}`],
                        minWidth: CARD_COLUMN_WIDTH,
                        maxWidth: columnWidths[`card-${card?.id}`],
                        userSelect: 'none',
                        textAlign: 'center',
                        padding: '5px 12px',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: card?.color || theme.palette.warning.main,
                            border: `1px solid ${theme.palette.divider}`,
                            flexShrink: 0,
                          }}
                        />
                        <span>{card?.name}</span>
                      </Box>
                      <Box
                        onMouseDown={(mouseEvent) => startResizing(`card-${card?.id}`, mouseEvent)}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          height: '100%',
                          width: '6px',
                          cursor: 'col-resize',
                          zIndex: 10,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
                        }}
                      />
                    </StyledTableHeadCell>
                  ))}
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {filteredEvents.map((event) => {
                  const slotRows = getSlotRows(event)

                  return (
                    <StyledTableRow report={false} key={event?.id}>
                      <StyledTableBodyCell
                        report={false}
                        sx={{
                          width: columnWidths[EVENT_COLUMN_KEY],
                          minWidth: EVENT_COLUMN_WIDTH,
                          maxWidth: columnWidths[EVENT_COLUMN_KEY],
                          padding: 0,
                          verticalAlign: 'top',
                          whiteSpace: 'normal',
                        }}
                      >
                        <Box sx={buildMatrixCellGridSx(slotRows.length)}>
                          <Box
                            sx={{
                              height: '100%',
                              boxSizing: 'border-box',
                              px: '15px',
                              py: EVENT_HEADER_PADDING_Y,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              ...buildSectionDividerSx(theme),
                            }}
                          >
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.palette.text.primary, marginBottom: 0.5, lineHeight: 1.35 }}>
                              {event?.name}
                            </Typography>
                            <ScopeChip eventId={event?.id} scopeType={event?.scope_type} />
                            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, lineHeight: 1.35 }}>
                              {formatDateRange(event)}
                            </Typography>
                          </Box>

                          {slotRows.map((slot, index) => (
                            <Box
                              key={slot?.id}
                              sx={{
                                height: '100%',
                                boxSizing: 'border-box',
                                display: 'flex',
                                alignItems: 'center',
                                px: '15px',
                                py: 1,
                                ...buildSlotDividerSx(theme, index),
                              }}
                            >
                              <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary, fontWeight: 500, lineHeight: 1.35 }}>
                                {slot?.placeholder ? 'No time slots' : formatSlotLabel(slot)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </StyledTableBodyCell>

                      {cardColumns.map((card) => (
                        <StyledTableBodyCell
                          report={false}
                          key={card?.id}
                          sx={{
                            width: columnWidths[`card-${card?.id}`],
                            minWidth: CARD_COLUMN_WIDTH,
                            maxWidth: columnWidths[`card-${card?.id}`],
                            padding: 0,
                            verticalAlign: 'top',
                          }}
                        >
                          <Box sx={buildMatrixCellGridSx(slotRows.length)}>
                            <Box
                              sx={{
                                height: '100%',
                                boxSizing: 'border-box',
                                py: EVENT_HEADER_PADDING_Y,
                                ...buildSectionDividerSx(theme),
                              }}
                            />

                            {slotRows.map((slot, index) => {
                              const slotCard = (slot?.cards || []).find((slotValue) => slotValue?.id === card?.id)
                              const cellKey = `${slot?.id}-${card?.id}`

                              return (
                                <Box
                                  key={`${slot?.id}-${card?.id}`}
                                  sx={{
                                    height: '100%',
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1.5,
                                    py: 1,
                                    ...buildSlotDividerSx(theme, index),
                                  }}
                                >
                                  {slotCard ? (
                                    <Switch
                                      checked={!slotCard?.blocked}
                                      onChange={(toggleEvent) => handleToggleChange(event, slot, card, toggleEvent.target.checked)}
                                      disabled={pendingCellKey === cellKey || !canUpdateDiscountCard(event)}
                                      color="success"
                                      inputProps={{
                                        'aria-label': `${event?.name} ${slot?.placeholder ? 'No time slots' : formatSlotLabel(slot)} ${card?.name}`,
                                      }}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: theme.palette.text.primary }}>-</Typography>
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                        </StyledTableBodyCell>
                      ))}
                    </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <Box ref={tableFooterRef} sx={{ display: 'flow-root' }}>
            <Box
              ref={fakeScrollRef}
              onScroll={handleFakeScroll}
              sx={scrollbarStyles}
            >
              <Box
                sx={{
                  width: scrollWidth || '100%',
                  height: '1px',
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
